import type { ProviderBranding } from '@app-iptv/iptv-client'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { reset as resetDetailOverlay } from './detail-overlay-store'


export type LoginMode = 'provider' | 'direct'

interface DirectConfig {
  listName: string
  serverUrl: string
}

export type LogoutReason = 'user' | 'session_expired' | 'account_expired'

/** Tipo das chaves de tipo do chip da busca (Tudo/Canais/Filmes/Séries).
 *  Espelha o FilterType local da SearchScreen — duplicado aqui de propósito
 *  pra o store não importar de api.ts (evita ciclo + mantém o store leve). */
export type SearchFilterTypeState = 'all' | 'channels' | 'movies' | 'series'

/** Filtros avançados da busca persistidos no store. Estrutura idêntica ao
 *  SearchFiltersClient da api.ts — redeclarado aqui pelo mesmo motivo do
 *  SearchFilterTypeState (sem import cruzado). A SearchScreen faz o cast
 *  trivial entre os dois (mesmos campos). */
export interface SearchFiltersState {
  genres?: string[]
  years?: string[]
  platforms?: string[]
  ageRatings?: string[]
  audios?: string[]
  qualities?: string[]
  minRating?: number
}

/**
 * Chave estável da conta para identificar perfis.
 * Combina código do provedor (ou listName direto) com username.
 */
export function accountKey(s: {
  providerCode: string | null
  username: string | null
  direct: { listName: string } | null
}): string | null {
  if (!s.username) return null
  if (s.providerCode) return `${s.providerCode}:${s.username}`
  if (s.direct) return `direct:${s.direct.listName}:${s.username}`
  return null
}

/**
 * Tipo da fonte ATIVA da conta Swipe, como ESTE aparelho a conhece — o mesmo
 * vocabulário do backend (`swipe_user_preferences.sourceType`).
 *
 * NÃO confundir com `mode` (LoginMode). `mode` diz por qual TELA o aparelho
 * resolveu a sessão ('provider' = /code+/login, 'direct' = /v1/auth/direct, o
 * caminho de DEVICE). `sourceType` diz o que a CONTA tem gravado. Os dois se
 * cruzam: a fonte MANUAL da conta também chega como `mode:'provider'`, porque o
 * login Xtream dela usa um código — só que um código EFÊMERO (`D{hash}`), gerado
 * pelo servidor e sem valor fora da conta. Foi exatamente essa colisão que fez a
 * presença de `providerCode` virar um gatilho errado (proxy) pra "esta fonte é
 * por código" (invariante).
 *
 * `null` = não sabemos (estado persistido por versão anterior, ou fonte resolvida
 * por um caminho que não informa). Na dúvida, o app NÃO escreve na conta.
 */
export type AccountSourceType = 'code' | 'manual'

interface SessionState {
  mode: LoginMode | null
  providerCode: string | null
  /** Tipo da fonte ativa da conta (ver `AccountSourceType`). */
  sourceType: AccountSourceType | null
  /**
   * A fonte acima foi ESCOLHIDA NESTE APARELHO (o usuário digitou o código ou
   * cadastrou a lista aqui) e ainda NÃO foi gravada na conta Swipe.
   *
   * É o que separa "o usuário está escolhendo uma fonte agora" de "este aparelho
   * apenas lembra a fonte de uma sessão anterior". Só o primeiro autoriza um
   * `PUT preference` — sem isso, todo relogin reescreveria a preferência da conta
   * com o que ESTE aparelho por acaso guardou, e um aparelho desatualizado
   * desfaria a escolha feita em outro (a conta é a fonte da verdade, o aparelho
   * não). Zerado por `markSourceSyncedToAccount()` quando a gravação CONFIRMA;
   * enquanto ela não confirmar, continua ligado e a próxima oportunidade tenta de
   * novo — a gravação é fire-and-forget, mas não é "uma chance só".
   *
   * PERSISTIDO de propósito: sem sessão Swipe com fonte, a conta fica em
   * `no_provider` e o app abre VAZIO (todas as rotas de catálogo respondem 401
   * no_provider). Perder a intenção num reload deixaria o usuário num loop de
   * configurar fonte que nunca grava.
   */
  sourceChosenHere: boolean
  branding: ProviderBranding | null
  direct: DirectConfig | null
  isAuthed: boolean
  username: string | null
  lastChannelCategoryId: string | null
  /** Último bucket selecionado na LiveScreen (3.7k revamp).
   *  'all' | 'favorites' | 'recents' | bucketId | providerCategoryId */
  lastLiveBucket: string | null
  /** Último canal focado na LiveScreen — restaura preview ao voltar do player.
   *  É a CHAVE do grupo (sources[0]), não a fonte: é ela que casa o exactGroup e
   *  restaura o foco. Para a FONTE, ver lastLiveOpenChannelId. */
  lastLiveChannelId: string | null
  /** A FONTE de fato aberta no player (resolve-open-source), ao lado da chave.
   *  Existe porque em t≈0 (POP: voltar do fullscreen) ainda não há catálogo,
   *  então não há grupo pra resolver — e sem isto o warm-up aquecia o sign da
   *  CHAVE (a fonte que não vai tocar num canal multi-fonte) e o preview tocava
   *  a chave crua até o catálogo chegar, trocando video.src no boot da tela.
   *  Zerada junto com a chave em todo reset de provider/logout. */
  lastLiveOpenChannelId: string | null
  lastVodCategoryId: string | null
  lastSeriesCategoryId: string | null
  /** Última aba do MoviesScreen ('recent' | 'categories' | 'mylist'). Pra
   *  voltar de details pro mesmo lugar. Default 'recent' (Descobrir). */
  lastMoviesTab: string | null
  /** Categoria do drill-down do Catálogo de Movies (null = browse de cards). */
  lastMoviesDrillId: string | null
  /** Última aba do SeriesScreen — análogo a lastMoviesTab. */
  lastSeriesTab: string | null
  /** Categoria do drill-down do Catálogo de Series (null = browse de cards). */
  lastSeriesDrillId: string | null
  // ===== Estado da BUSCA (fix/busca-estado-perdido) =====
  // Espelha os campos lastXxx das telas irmãs: ao abrir um item da Search e
  // voltar (BACK/POP), a tela remontava com tudo zerado. Persistir aqui faz a
  // query/filtros/foco sobreviverem ao remount (TV) e ao goBack (mobile).
  // Provider-scoped por reset: ZERADOS junto dos lastXxx em setProvider/
  // setDirect/clearLoginStage/logout/reset (anti-vazamento entre providers).
  /** Termo da busca (debounced/commitado) pra reidratar input + disparar query. */
  lastSearchQuery: string | null
  /** Chip de tipo ativo (Tudo/Canais/Filmes/Séries). */
  lastSearchFilterType: SearchFilterTypeState | null
  /** Filtros avançados aplicados (gênero/ano/qualidade/etc). */
  lastSearchFilters: SearchFiltersState | null
  /** focusKey do card aberto (s-ch-/s-mv-/s-sr-${id}) pra restaurar o foco no
   *  card ao voltar — decisão do Fernando: foco volta pro CARD, não pro topo. */
  lastSearchFocusKey: string | null
  accountExpiresAt: number | null
  flashError: string | null // mensagem a mostrar na próxima tela de login
  activeProfileId: string | null
  activeProfileKind: 'adult' | 'kids' | null
  lastProfileIdByAccount: Record<string, string>
  /**
   * Pareamento QR CONDUZINDO no gate (fix janela transiente file://). Ligado no
   * início do `finalizeLogin` e mantido enquanto o gate deve permanecer montado
   * (finalizando, ou parado em needsPreference/erro esperando o usuário). Durante
   * o finalize o store muda em passos (setProvider → api.login → setAuth), cada um
   * re-renderizando o RootRoute ENQUANTO `isAuthed` ainda é false — e sem este
   * flag o RootRoute (caso 1 base instalada, ou caso 3 fresh install) navegava pra
   * fora, DESMONTANDO o gate e abortando o pairing (bug: TV caía em user/senha
   * Xtream após o QR aprovado). Com o flag ligado, o RootRoute mantém o gate no ar
   * (guarda no TOPO do TvRoot). Desligado no `setAuth` (sucesso), no cancel/
   * configureHere/retry (usuário sai/reinicia), no logout/reset e no cleanup do
   * hook. NÃO é persistido: transitório de UI; num reload no meio, o boot reseta
   * pra false e o RootRoute roteia normal (sessão Swipe persiste; sem fonte → caso
   * 3 de config; com fonte + auth persistidos → caso 1). Nunca concede acesso.
   */
  pairingFinalizing: boolean

  /**
   * A que FONTE o estado de navegação provider-scoped (os `lastXxx` + os
   * `lastSearchXxx`) pertence. É o código do provedor dono desses IDs, ou `null`
   * quando eles não são de ninguém (recém-zerados, modo direto, ou instalação
   * anterior a este campo).
   *
   * ── Por que ele existe ──
   * Porque adotar uma fonte e ZERAR o progresso deixaram de ser o mesmo ato. O
   * `setProvider` acontece no submit da tela do código, quando ninguém ainda
   * sabe se o código é real: desde que `GET /v1/provider/:code/branding` responde
   * 200 pra qualquer código (frente do oráculo de enumeração), errar uma letra no
   * controle remoto avançava e apagava o "continuar de onde parei" — e o usuário
   * só descobria o erro um passo depois, no login. Sem este campo não dá pra
   * adiar o zerar, porque no momento da confirmação o `providerCode` já é o NOVO
   * e o dono do estado antigo teria se perdido.
   *
   * ── O invariante que ele guarda ──
   * O anti-vazamento entre provedores continua absoluto, só que ancorado na
   * CONSEQUÊNCIA ("a fonte ativa confirmada mudou") em vez do PROXY ("o usuário
   * passou pela tela de escolher fonte"). Quem passa pela tela pode voltar; quem
   * confirmou, não.
   *
   * ── Lado seguro ──
   * `null` nunca casa com um `providerCode` real, então instalação antiga (ou
   * estado recém-limpo) ZERA na próxima confirmação. O default erra para o lado
   * de apagar demais, nunca para o de vazar.
   */
  navScopeCode: string | null

  /**
   * De QUAL conta Swipe (`swipeUserId`) é o contexto de conta deste store: a fonte
   * (`providerCode`/`username`), o perfil ativo e, por eles, o escopo do local-db.
   * `null` = de ninguém conhecido (deslogado, base instalada sem conta, ou instalação
   * anterior a este campo).
   *
   * ── Por que existe (cybersec P1, 10/10/2026) ──
   * A sessão Swipe e este contexto moram em lugares diferentes e morriam em momentos
   * diferentes: um 401 no refresh apaga só a sessão (sync/swipe-session.ts), e o contexto
   * de A seguia no store. A conta B entrava e montava o app com o perfil e o histórico
   * local de A. Agora o contexto carrega o dono, e `context-owner.ts` o derruba
   * (`dropAccountContext`) no instante em que a sessão deixa de ser dele.
   */
  contextOwner: string | null

  /**
   * Adota uma fonte de CONTA neste aparelho.
   *
   * O 3º parâmetro é OBRIGATÓRIO e não tem default de propósito: o defeito que
   * ele fecha nasceu de um caminho novo (fonte manual) herdando, em silêncio, a
   * semântica do caminho antigo (fonte por código). Quem adiciona um caminho novo
   * tem que declarar o que ele é — se houvesse default, o próximo caminho voltaria
   * a herdar errado e nada quebraria no typecheck.
   *
   *  - `type`: o que a CONTA tem gravado (ver `AccountSourceType`).
   *  - `chosenHere`: o usuário ESCOLHEU esta fonte agora, neste aparelho? Só
   *    `true` autoriza gravar na conta depois (ver `sourceChosenHere`). Fluxos
   *    que apenas LEEM a fonte da conta (pareamento por QR) passam `false`.
   */
  setProvider(
    code: string,
    branding: ProviderBranding,
    source: { type: AccountSourceType; chosenHere: boolean },
  ): void
  setDirect(listName: string, serverUrl: string, branding: ProviderBranding): void
  /** Confirma que a fonte escolhida aqui já está gravada na conta (zera o
   *  `sourceChosenHere`). Chamar SÓ quando o backend confirmar a gravação. */
  markSourceSyncedToAccount(): void
  /** Atualiza só o branding sem zerar lastCategory/etc. Usado pelo
   *  refresh em background — admin do parceiro pode mudar cor/logo
   *  online e cliente reflete sem logout. NÃO troca mode/providerCode. */
  updateBranding(branding: ProviderBranding): void
  /** Reseta APENAS o "stage" pré-login (mode/providerCode/branding/direct).
   *  Usado em BACK das telas /code e /direct quando user ainda não logou
   *  — sem isso, RootRoute vê mode setado e redireciona pra /login num
   *  loop infinito ao tentar voltar pra Welcome.
   *  NÃO toca isAuthed/username/perfis (esses só zeram via logout/reset). */
  clearLoginStage(): void
  setAuth(username: string, accountExpiresAt?: number | null): void
  /**
   * Confirma a fonte da CONTA como CONTEXTO deste aparelho (escopo do local-db, perfis e
   * sync) SEM autenticá-lo como sessão Xtream: `isAuthed` fica como está. É o terminador
   * do `setProvider` na superfície MOBILE (account-context.ts), que entra pela conta
   * Swipe e nunca faz `api.login`. `isAuthed` lá ligaria o snapshot do catálogo inteiro,
   * o refresh de 30 min e a medição de banda da TV num celular.
   *
   * Mesmo anti-vazamento do `setAuth` (zera a navegação quando a fonte confirmada muda)
   * e, além dele, zera o perfil ativo quando a CONTA (fonte ou usuário) muda: o perfil
   * de outra conta não pode seguir ativo. Reconfirmar a mesma conta preserva o perfil
   * (o boot do mobile chama isto a cada abertura).
   */
  setAccountContext(username: string): void
  setLastChannelCategoryId(id: string | null): void
  setLastLiveBucket(id: string | null): void
  setLastLiveChannelId(id: string | null): void
  setLastLiveOpenChannelId(id: string | null): void
  setLastVodCategoryId(id: string | null): void
  setLastSeriesCategoryId(id: string | null): void
  setLastMoviesTab(tab: string | null): void
  setLastMoviesDrillId(id: string | null): void
  setLastSeriesTab(tab: string | null): void
  setLastSeriesDrillId(id: string | null): void
  setLastSearchQuery(q: string | null): void
  setLastSearchFilterType(type: SearchFilterTypeState | null): void
  setLastSearchFilters(filters: SearchFiltersState | null): void
  setLastSearchFocusKey(key: string | null): void
  setFlashError(msg: string | null): void
  setActiveProfile(profile: { id: string; kind: 'adult' | 'kids' } | null): void
  /** Liga/desliga o flag de "pareamento QR finalizando" (ver campo acima). */
  setPairingFinalizing(v: boolean): void
  logout(reason?: LogoutReason): void
  reset(): void
  /** Grava o dono do contexto (ver `contextOwner`). Chamado na hidratação pela conta. */
  setContextOwner(owner: string | null): void
  /**
   * Derruba TODO o contexto de conta (fonte, usuário, perfil ativo, navegação) e o dono.
   * É o `reset()` menos o `pairingFinalizing`, que pertence a um pareamento em curso
   * e não à conta que saiu. Usado quando a sessão Swipe deixa de ser do dono do contexto.
   */
  dropAccountContext(): void
}

/**
 * O estado de navegação que pertence a UMA fonte — zerado sempre inteiro.
 *
 * A lista morava copiada em cinco blocos de reset. Um campo acrescentado a um
 * deles e esquecido nos outros quatro era vazamento silencioso entre provedores
 * (IDs de categoria/canal do provedor A abrindo no catálogo do B). Com um dono
 * só, esse modo de falha específico deixou de existir: não há "outros quatro".
 *
 * ⚠️ O QUE O `Pick` GARANTE, e o que ele NÃO garante. Medido nos dois sentidos,
 * não deduzido:
 *
 *  - Nome na lista sem valor no literal, ou nome escrito errado: ERRO DE
 *    COMPILAÇÃO (`TS2741` / `TS2561`). É consistência INTERNA: o objeto tem
 *    exatamente as chaves que a lista declara.
 *  - Campo novo em `SessionState` que ninguém acrescentou nem à lista nem ao
 *    literal: **COMPILA VERDE**. Isso é COMPLETUDE (a lista cobrir tudo que é
 *    provider-scoped), e o compilador não tem como cobrar: "pertence a uma
 *    fonte" é propriedade semântica, não estrutural. Nenhum `Pick`, `Record` ou
 *    mapped type decide isso por nós.
 *
 * Quem cobra a COMPLETUDE é a varredura DERIVADA em `store.test.ts`
 * ("todo campo `last*` do estado inicial zera em todos os pontos de reset"), que
 * enumera o inventário real do store em vez de reler esta lista. Os dois lados
 * são necessários: este `Pick` é o piso nominal (pega deleção e erro de grafia),
 * a varredura pega o campo que ninguém lembrou de classificar. Sozinho, cada um
 * tem um buraco do tamanho do outro.
 *
 * Quem consome: `setDirect`, `clearLoginStage`, `logout`, `reset` (zeram na hora),
 * `setAuth` (zera só quando a fonte confirmada muda) e o `merge` do persist (zera
 * na reidratação quando o dono não bate com a fonte ativa). `setProvider` NÃO
 * consome, e isso é a correção: ver o bloco dele.
 */
const PROVIDER_SCOPED_NAV_RESET: Pick<
  SessionState,
  | 'lastChannelCategoryId'
  | 'lastLiveBucket'
  | 'lastLiveChannelId'
  | 'lastLiveOpenChannelId'
  | 'lastVodCategoryId'
  | 'lastSeriesCategoryId'
  | 'lastMoviesTab'
  | 'lastMoviesDrillId'
  | 'lastSeriesTab'
  | 'lastSeriesDrillId'
  | 'lastSearchQuery'
  | 'lastSearchFilterType'
  | 'lastSearchFilters'
  | 'lastSearchFocusKey'
> = {
  // IDs de categoria/canal são POR PROVIDER — não cruzam.
  lastChannelCategoryId: null,
  lastLiveBucket: null,
  lastLiveChannelId: null,
  lastLiveOpenChannelId: null,
  lastVodCategoryId: null,
  lastSeriesCategoryId: null,
  lastMoviesTab: null,
  lastMoviesDrillId: null,
  lastSeriesTab: null,
  lastSeriesDrillId: null,
  // Busca segue o mesmo critério: termos e IDs de card são do provider que sai.
  lastSearchQuery: null,
  lastSearchFilterType: null,
  lastSearchFilters: null,
  lastSearchFocusKey: null,
}

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      mode: null,
      providerCode: null,
      sourceType: null,
      sourceChosenHere: false,
      branding: null,
      direct: null,
      isAuthed: false,
      username: null,
      lastChannelCategoryId: null,
      lastLiveBucket: null,
      lastLiveChannelId: null,
      lastLiveOpenChannelId: null,
      lastVodCategoryId: null,
      lastSeriesCategoryId: null,
      lastMoviesTab: null,
      lastMoviesDrillId: null,
      lastSeriesTab: null,
      lastSeriesDrillId: null,
      lastSearchQuery: null,
      lastSearchFilterType: null,
      lastSearchFilters: null,
      lastSearchFocusKey: null,
      accountExpiresAt: null,
      flashError: null,
      activeProfileId: null,
      activeProfileKind: null,
      lastProfileIdByAccount: {},
      pairingFinalizing: false,
      navScopeCode: null,
      contextOwner: null,

      /**
       * Adota a fonte — e SÓ isso. Não zera o estado de navegação, de propósito.
       *
       * ── O que zerava aqui e por que saiu ──
       * Este reducer roda no submit da tela do código, que é ANTES de qualquer
       * confirmação de que o código existe. Enquanto a rota de branding devolvia
       * 404 pra código desconhecido, o `getBranding` barrava o erro de digitação
       * antes daqui e o zerar-junto passava despercebido. A rota passou a
       * responder 200 sempre (frente do oráculo de enumeração, apps/api/src/
       * routes/public.ts) e o portão sumiu: qualquer string de 4 a 8 caracteres
       * chega aqui. Errar uma letra no controle remoto apagava o "continuar de
       * onde parei" de um aparelho que talvez nem fosse trocar de fonte — o
       * usuário só descobria o engano um passo depois, no login.
       *
       * ── Onde o zerar foi parar ──
       * No `setAuth`, a CONFIRMAÇÃO: é lá que a fonte trocou de verdade, e é lá
       * que o anti-vazamento entre provedores tem que valer. Passar por esta tela
       * não é trocar de fonte; confirmar é.
       *
       * São TRÊS os fluxos que chamam `setProvider`, e o comando que os enumera
       * (o número já esteve errado neste comentário — dizia quatro, contando o
       * relogin, que chama `setAuth` e não `setProvider`):
       *
       *   git grep -n "setProvider(" -- apps/tv/src ':!*.test.*' ':!*store.ts' \
       *     | grep -v "const setProvider"
       *   # hoje: account-context.ts (a fonte da CONTA: pareamento QR da TV e
       *   #       superfície mobile) · ManualSourceScreen.tsx (fonte manual) ·
       *   #       provider-code-submit.ts (tela do código)
       *   #       + a assinatura da interface, no mesmo provider-code-submit.ts
       *
       * Todos terminam em `setAuth`, menos UM, e por desenho: o caminho mobile do
       * account-context.ts termina em `setAccountContext`, que faz o MESMO reset da
       * navegação (e do perfil ativo, se a conta mudou) sem ligar `isAuthed`. Um
       * caminho que não termine em nenhum dos dois é vazamento entre provedores, e o
       * comando acima é o falsificador.
       *
       * ── A janela entre adotar e confirmar, e por que ela tem DOIS guardas ──
       * Enquanto o usuário está em `/login`, nenhuma tela de catálogo monta e os
       * `lastXxx` só são lidos no mount delas. Se ele DESISTIR (BACK), o catálogo
       * que continua servido é o da fonte antiga, que é justamente a dona dos IDs
       * preservados: zerar cedo tornava esse caso PIOR, apagando o progresso de
       * uma troca que nunca aconteceu.
       *
       * ⚠️ Mas essa janela é PERSISTIDA (`providerCode` novo, `navScopeCode` e
       * `lastXxx` antigos), e "não dá pra alcançar catálogo sem `setAuth`" é falso
       * num caso: REINICIAR o app dentro da janela com `isAuthed` já true faz o
       * `RootRoute` ir direto ao catálogo, e `setAuth` nunca roda. Hoje isso é
       * inalcançável porque `/code` exige `isAuthed === false` (RootRoute redireciona
       * quando true) e os dois caminhos pra false, `logout` e `reset`, já zeram. Mas
       * isso é propriedade de OUTRO ARQUIVO: o dia em que Ajustes ganhar "trocar de
       * provedor" logado (plano A3, `routes.tsx`), a janela vira alcançável. Por isso
       * o segundo guarda, no `merge` do persist: a reidratação reconcilia dono e
       * fonte. Ver o bloco `merge` lá embaixo.
       */
      setProvider: (code, branding, source) =>
        set({
          mode: 'provider',
          providerCode: code,
          sourceType: source.type,
          sourceChosenHere: source.chosenHere,
          branding,
          direct: null,
        }),
      updateBranding: (branding) => set({ branding }),
      markSourceSyncedToAccount: () => set({ sourceChosenHere: false }),
      setDirect: (listName, serverUrl, branding) =>
        set({
          mode: 'direct',
          direct: { listName, serverUrl },
          branding,
          providerCode: null,
          // Caminho de DEVICE (/v1/auth/direct): não existe fonte de CONTA aqui.
          // Deixar um sourceType antigo pendurado faria o RootRoute e o gate de
          // escrita raciocinarem sobre uma fonte que este aparelho não usa mais.
          sourceType: null,
          sourceChosenHere: false,
          // Zera na hora, e não na confirmação como o caminho por código: aqui a
          // troca de fonte JÁ aconteceu (o `setDirect` só roda depois de o
          // `/v1/auth/direct` validar), e `providerCode` é `null` nos dois lados
          // de uma troca entre duas listas diretas — `navScopeCode` não teria como
          // distingui-las. Adiar aqui seria adiar sem ganho e com vazamento.
          ...PROVIDER_SCOPED_NAV_RESET,
          navScopeCode: null,
        }),
      /**
       * A CONFIRMAÇÃO da fonte — e, por isso, o ponto onde o estado de navegação
       * do provedor que sai morre.
       *
       * O gatilho é o INVARIANTE ("a fonte ativa confirmada é outra?"), não o
       * PROXY ("o usuário mexeu na escolha de fonte"). Reconfirmar a MESMA fonte
       * — relogin, sessão expirada, reconfigurar e desistir — não é troca, e
       * preserva. `navScopeCode` null (instalação antiga, estado recém-limpo)
       * nunca casa com um código real: erra pro lado de zerar.
       */
      setAuth: (username, accountExpiresAt = null) =>
        set((s) => {
          // Chave de escopo: o código do provedor. `null` no modo direto, que já
          // zerou no `setDirect` — ver a justificativa lá.
          const escopo = s.providerCode
          const trocouDeFonte = s.navScopeCode !== escopo
          return {
            isAuthed: true,
            username,
            accountExpiresAt,
            // Login novo sempre força reseleção de perfil
            activeProfileId: null,
            activeProfileKind: null,
            // Autenticou → o pairing terminou com sucesso. Desligar no MESMO set()
            // (átomo com isAuthed=true) evita um render intermediário onde a guarda
            // ainda seguraria o gate: o próximo render já cai no caso 1 → /profiles.
            pairingFinalizing: false,
            navScopeCode: escopo,
            ...(trocouDeFonte ? PROVIDER_SCOPED_NAV_RESET : {}),
          }
        }),
      setAccountContext: (username) =>
        set((s) => {
          const escopo = s.providerCode
          const trocouDeFonte = s.navScopeCode !== escopo
          const trocouDeConta = trocouDeFonte || s.username !== username
          return {
            username,
            navScopeCode: escopo,
            ...(trocouDeFonte ? PROVIDER_SCOPED_NAV_RESET : {}),
            ...(trocouDeConta ? { activeProfileId: null, activeProfileKind: null } : {}),
          }
        }),
      clearLoginStage: () =>
        set({
          mode: null,
          providerCode: null,
          // O stage pré-login é justamente a escolha de fonte em curso: limpar
          // mode/providerCode e deixar sourceType/sourceChosenHere vivos deixaria
          // a intenção de gravar sem a fonte a que ela se referia.
          sourceType: null,
          sourceChosenHere: false,
          branding: null,
          direct: null,
          // Zera "lasts" também — IDs eram do provider antigo. Se user
          // troca de provider via BACK→novo code, sem zerar isso ele
          // tentaria abrir categoria fantasma (ID inexistente no novo).
          // Mesmo critério usado pelo `logout()`.
          //
          // Quem chega aqui está DESLOGADO (os três chamadores testam `!isAuthed`
          // antes) — e deslogar já passou pelo `logout`, que zerou. Ou seja: na
          // prática este reset é redundante, e é por isso que ele fica. O caminho
          // que tinha progresso a perder era o LOGADO (reconfigurar fonte), que
          // não passa por aqui; abrir mão da redundância neste bloco não devolvia
          // nada ao usuário e só afrouxaria o anti-vazamento.
          ...PROVIDER_SCOPED_NAV_RESET,
          navScopeCode: null,
        }),
      setLastChannelCategoryId: (id) => set({ lastChannelCategoryId: id }),
      setLastLiveBucket: (id) => set({ lastLiveBucket: id }),
      setLastLiveChannelId: (id) => set({ lastLiveChannelId: id }),
      setLastLiveOpenChannelId: (id) => set({ lastLiveOpenChannelId: id }),
      setLastVodCategoryId: (id) => set({ lastVodCategoryId: id }),
      setLastSeriesCategoryId: (id) => set({ lastSeriesCategoryId: id }),
      setLastMoviesTab: (tab) => set({ lastMoviesTab: tab }),
      setLastMoviesDrillId: (id) => set({ lastMoviesDrillId: id }),
      setLastSeriesTab: (tab) => set({ lastSeriesTab: tab }),
      setLastSeriesDrillId: (id) => set({ lastSeriesDrillId: id }),
      setLastSearchQuery: (q) => set({ lastSearchQuery: q }),
      setLastSearchFilterType: (type) => set({ lastSearchFilterType: type }),
      setLastSearchFilters: (filters) => set({ lastSearchFilters: filters }),
      setLastSearchFocusKey: (key) => set({ lastSearchFocusKey: key }),
      setFlashError: (msg) => set({ flashError: msg }),
      setPairingFinalizing: (v) => set({ pairingFinalizing: v }),
      setActiveProfile: (profile) =>
        set((s) => {
          if (!profile) {
            return { activeProfileId: null, activeProfileKind: null }
          }
          const account = accountKey(s)
          return {
            activeProfileId: profile.id,
            activeProfileKind: profile.kind,
            lastProfileIdByAccount: account
              ? { ...s.lastProfileIdByAccount, [account]: profile.id }
              : s.lastProfileIdByAccount,
          }
        }),
      // PRESERVA mode/providerCode/branding/direct — e, pela mesma razão,
      // sourceType/sourceChosenHere (ausentes deste `set` de propósito): o
      // RootRoute usa esse conjunto pra devolver o usuário à tela certa de
      // relogin. Preservar `providerCode` SEM preservar `sourceType` era o buraco
      // do P0: sobrava o código (efêmero) sem a informação de que ele era de uma
      // fonte MANUAL, e o relogin gravava a fonte errada na conta.
      logout: (reason = 'user') => {
        // A ficha-overlay (e o player dentro dela) vive FORA da árvore roteada:
        // o `DetailOverlayHost` é IRMÃO do `RouterProvider` e renderiza enquanto
        // houver camada na pilha. Nenhum guard de rota o desmonta — então um 401
        // com ficha aberta navegava pro gate com o conteúdo ainda por cima. Não é
        // porta anônima; é CONTEÚDO SOBREVIVENDO À FRONTEIRA.
        //
        // Aqui e não no `signOut()` porque este é o ponto ÚNICO por onde as três
        // saídas passam: `signOut()` (linha final), o expiry proativo do
        // AuthGuard e o `setOnAuthInvalid` do bootstrap (401/403 do backend).
        // Pendurar no `signOut()` cobriria só a saída deliberada.
        //
        // O `reset()` já existia com a intenção escrita na própria doc
        // ("deep-link com replace, hard-nav, logout") e ZERO chamador em
        // produção — mais uma defesa afirmada que o código não tinha.
        resetDetailOverlay()
        set({
          isAuthed: false,
          username: null,
          accountExpiresAt: null,
          activeProfileId: null,
          activeProfileKind: null,
          // Limpa "última categoria" — apontam pra IDs do provider que
          // talvez não existam no próximo. Sem isso, próximo login pode
          // tentar carregar categoria fantasma.
          ...PROVIDER_SCOPED_NAV_RESET,
          // Zerou o estado, então ele não é mais de ninguém. Deixar o dono antigo
          // pendurado faria a próxima confirmação da MESMA fonte pular o reset —
          // inofensivo hoje (já está vazio), mentiroso amanhã.
          navScopeCode: null,
          // O contexto não é mais de conta nenhuma (o `username` e o perfil morreram aqui).
          contextOwner: null,
          // Deslogou → nenhum pareamento em finalização (não deixar preso ligado).
          pairingFinalizing: false,
          flashError:
            reason === 'session_expired'
              ? 'errors.sessionExpired'
              : reason === 'account_expired'
                ? 'errors.accountExpired'
                : null,
        })
      },
      reset: () =>
        set({
          mode: null,
          providerCode: null,
          sourceType: null,
          sourceChosenHere: false,
          branding: null,
          direct: null,
          isAuthed: false,
          username: null,
          ...PROVIDER_SCOPED_NAV_RESET,
          navScopeCode: null,
          accountExpiresAt: null,
          flashError: null,
          activeProfileId: null,
          activeProfileKind: null,
          pairingFinalizing: false,
          contextOwner: null,
        }),
      setContextOwner: (owner) => set({ contextOwner: owner }),
      dropAccountContext: () => {
        // A ficha-overlay (e o player dentro dela) vive FORA da árvore roteada: sem isto,
        // a sessão revogada no meio do vídeo derrubava a rota mas o player seguia por cima
        // (mesma razão do `logout`; decisão do dono: revogação INTERROMPE na hora).
        resetDetailOverlay()
        set({
          mode: null,
          providerCode: null,
          sourceType: null,
          sourceChosenHere: false,
          branding: null,
          direct: null,
          isAuthed: false,
          username: null,
          ...PROVIDER_SCOPED_NAV_RESET,
          navScopeCode: null,
          accountExpiresAt: null,
          activeProfileId: null,
          activeProfileKind: null,
          contextOwner: null,
        })
      },
    }),
    {
      name: 'iptv:session',
      partialize: (state) => ({
        mode: state.mode,
        providerCode: state.providerCode,
        // Persistidos junto de mode/providerCode porque descrevem a MESMA coisa
        // (a fonte deste aparelho) e porque o `logout` os preserva de propósito.
        // Instalação anterior a este campo reidrata sem ele → cai no default
        // `null`/`false`, que é o lado seguro: não sabemos, não escrevemos.
        sourceType: state.sourceType,
        sourceChosenHere: state.sourceChosenHere,
        branding: state.branding,
        direct: state.direct,
        isAuthed: state.isAuthed,
        username: state.username,
        lastChannelCategoryId: state.lastChannelCategoryId,
        lastLiveBucket: state.lastLiveBucket,
        lastLiveChannelId: state.lastLiveChannelId,
        lastLiveOpenChannelId: state.lastLiveOpenChannelId,
        lastVodCategoryId: state.lastVodCategoryId,
        lastSeriesCategoryId: state.lastSeriesCategoryId,
        lastMoviesTab: state.lastMoviesTab,
        lastMoviesDrillId: state.lastMoviesDrillId,
        lastSeriesTab: state.lastSeriesTab,
        lastSeriesDrillId: state.lastSeriesDrillId,
        lastSearchQuery: state.lastSearchQuery,
        lastSearchFilterType: state.lastSearchFilterType,
        lastSearchFilters: state.lastSearchFilters,
        lastSearchFocusKey: state.lastSearchFocusKey,
        // Persistido junto do estado que ele descreve — os `lastXxx` acima
        // sobrevivem ao restart, então o dono deles também precisa. Sem isto o
        // campo reidrataria `null` e a primeira confirmação depois de todo
        // restart zeraria um progresso que ninguém trocou de fonte pra perder.
        // Instalação anterior a este campo reidrata sem ele → `null`, que não
        // casa com nenhum código real → o `merge` do persist zera NO PRÓPRIO
        // BOOT (não "na próxima confirmação", como esta linha já disse: o
        // `setAuth` sequer roda nesse caminho, porque quem atualiza o app estando
        // logado vai direto ao catálogo). É o lado seguro escolhido: custa um
        // "continuar de onde parei" na primeira abertura pós-atualização, e nunca
        // vaza IDs entre provedores. Coberto por store.rehydrate-scope.test.ts
        // ("instalação ANTIGA (sem o campo no disco) zera uma vez").
        navScopeCode: state.navScopeCode,
        // Persistido junto do contexto que ele descreve: o boot seguinte confere o dono
        // contra a sessão do disco ANTES de montar o app (bootstrap → context-owner).
        contextOwner: state.contextOwner,
        accountExpiresAt: state.accountExpiresAt,
        // Persiste perfil ativo pra sobreviver F5 / restart de app.
        // É zerado no setAuth (login novo) e no logout — assim a regra
        // "sempre escolher ao logar" continua valendo.
        activeProfileId: state.activeProfileId,
        activeProfileKind: state.activeProfileKind,
        lastProfileIdByAccount: state.lastProfileIdByAccount,
      }),

      /**
       * RECONCILIAÇÃO DE BOOT: o dono do estado de navegação tem que ser a fonte
       * ativa. Se o disco discorda de si mesmo, o estado cai.
       *
       * ── Por que existe ──
       * Mover o reset de `setProvider` pra `setAuth` abriu uma janela PERSISTIDA:
       * entre adotar a fonte (submit da tela do código) e confirmá-la (login), o
       * disco guarda `providerCode = B` com `navScopeCode = A` e `lastXxx` de A.
       * Enquanto o usuário passa pelo login isso é inofensivo e até desejável (é o
       * que devolve o progresso a quem errou uma letra e voltou). Mas REINICIAR o
       * app dentro dessa janela com `isAuthed` já true faz o `RootRoute` cair no
       * caso 1 e ir direto ao catálogo: `setAuth` nunca roda, e os IDs de A ficam
       * pendurados sob o provedor B.
       *
       * Hoje o caso é INALCANÇÁVEL, e a razão está fora deste arquivo: `/code` só
       * é atingível com `isAuthed === false` (o `RootRoute` redireciona quando é
       * true), e os dois caminhos pra false (`logout`, `reset`) já zeram. Foi por
       * depender de propriedade de outro arquivo, que nenhum teste afirmava, que
       * este guarda entrou: o anti-vazamento era LOCAL e TOTAL no `setProvider`, e
       * a mudança o deixou PARCIAL e à distância. `routes.tsx` já declara o plano
       * de reabrir `/code` logado ("configurar fonte da minha conta"); no dia em
       * que isso acontecer, o defeito nasce da COMPOSIÇÃO rota + store, e nenhum
       * teste de store sozinho o pegaria.
       *
       * ── Por que `isAuthed` é parte da condição, e não só o par dono/fonte ──
       * Sem sessão resolvida não há catálogo a vazar: o `RootRoute` manda pro gate
       * ou pro login, e ali o `setAuth` decide certo (mesma fonte preserva, outra
       * zera). Incluir `isAuthed` é o que preserva a tese desta entrega: quem
       * errou o código, fechou o app e voltou pra corrigir NÃO perde o progresso,
       * porque está deslogado. Zerar toda reidratação discordante custaria esse
       * caso sem fechar nada a mais.
       *
       * ── Por que isto NÃO contradiz "avançar deixou de ser zerar" ──
       * Reidratar não é avançar. Avançar é um ato do usuário sobre um código que
       * ninguém verificou ainda; reidratar é a constatação, no boot, de que o dono
       * gravado e a fonte ativa não são o mesmo. Aí um dos dois está velho, e o
       * lado seguro é largar a navegação, nunca deixá-la vazar de provedor.
       *
       * `merge` e não `onRehydrateStorage` porque o `merge` é o ponto documentado
       * que DEVOLVE o estado final; o callback pós-hidratação só observa, e
       * corrigir por mutação lá dependeria de detalhe interno do zustand.
       */
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<SessionState>) }
        if (merged.isAuthed && merged.navScopeCode !== merged.providerCode) {
          return {
            ...merged,
            ...PROVIDER_SCOPED_NAV_RESET,
            navScopeCode: merged.providerCode,
          }
        }
        return merged
      },
    },
  ),
)
